import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Directions as DirectionsIcon,
  EventAvailable as EventAvailableIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CampaignService from '../../services/CampaignService';
// import tenantService from '../../services/TenantService';  // TenantService is not available
import { CampaignCreate } from '../../types/Campaign';
// import { OrganizationDTO } from '../../types/Tenant';  // Tenant types are not available

const CreateFirstCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Temporarily using empty organizations array since TenantService is not available
  const [organizations] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loadingOrgs] = useState(false);

  // Use CampaignService directly since it's not a class
  const campaignService = CampaignService;

  // Load all organizations - TenantService is not available
  // useEffect(() => {
  //   const fetchOrganizations = async () => {
  //     setLoadingOrgs(true);
  //     try {
  //       const orgs = await tenantService.getOrganizations();
  //       setOrganizations(orgs);
  //     } catch (err) {
  //       console.error('Error loading organizations:', err);
  //       setError('Unable to load organizations. Please try again later.');
  //     } finally {
  //       setLoadingOrgs(false);
  //     }
  //   };

  //   fetchOrganizations();
  // }, []);

  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    setSelectedOrgId(event.target.value);
  };

  const handleCreateCampaign = async () => {
    if (isCreating || !selectedOrgId) return;

    setIsCreating(true);
    setError(null);

    try {
      // Create a new campaign with required fields
      const newCampaign: CampaignCreate = {
        campaign_name: 'New Mentorship Campaign',
        organization_id: selectedOrgId
      };

      const campaign = await campaignService.createCampaign(selectedOrgId, newCampaign);

      if (!campaign) {
        throw new Error('Failed to create campaign');
      }

      // Navigate to the campaign overview with the new ID
      navigate(`/campaigns/${selectedOrgId}/${campaign.id}`);

    } catch (error) {
      console.error('Error creating campaign:', error);
      setError('Failed to create campaign. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Temporarily set to false since we're not loading organizations
  const showOrgSelector = false;

  return (
    <Paper sx={{ p: 4, borderRadius: 2, mb: 4 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Create Your First Campaign
      </Typography>
      <Typography variant="body1" align="center" paragraph sx={{ mb: 4 }}>
        Now that you have your organization set up, it's time to create your first mentorship campaign.
        Campaigns are mentorship programs where mentors and mentees can apply and be matched.
      </Typography>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <GroupsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Define Your Program
              </Typography>
              <Typography variant="body2" align="center">
                Set goals, duration, and focus areas for your mentorship program.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <DirectionsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Customize Applications
              </Typography>
              <Typography variant="body2" align="center">
                Create tailored applications for mentors and mentees to collect relevant information.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <EventAvailableIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography gutterBottom variant="h6" component="h2" align="center">
                Set Campaign Timeline
              </Typography>
              <Typography variant="body2" align="center">
                Define key dates for applications, matching, and the program duration.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        {showOrgSelector && (
          <FormControl sx={{ mb: 3, minWidth: 300 }}>
            <InputLabel id="organization-select-label">Organization</InputLabel>
            <Select
              labelId="organization-select-label"
              id="organization-select"
              value={selectedOrgId}
              onChange={handleOrgChange}
              label="Organization"
              disabled={loadingOrgs || isCreating}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select the organization for this campaign
            </FormHelperText>
          </FormControl>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          size="large"
          endIcon={isCreating ? <CircularProgress size={24} color="inherit" /> : <NavigateNextIcon />}
          onClick={handleCreateCampaign}
          disabled={isCreating || loadingOrgs || !selectedOrgId}
        >
          {isCreating ? 'Creating Campaign...' : 'Create Your Campaign'}
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateFirstCampaign;
